import React, { useState, useEffect } from 'react';
import {
  MdOutlineKeyboardArrowLeft,
  MdOutlineKeyboardArrowRight,
  MdCircle
} from "react-icons/md";
import { FaUser } from "react-icons/fa";
import '../../css/UserAdmin/NoticeBoard.css';

const NoticeBoard = () => {
  const [notices, setNotices] = useState([]);
  const [currentNoticeIndex, setCurrentNoticeIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNotices = async () => {
      setLoading(true);
      setError(null);

      // 1. Get user role AND userID from localStorage
      const userRole = localStorage.getItem('role') || 'Guest';
      const userID = localStorage.getItem('userID') || null; // e.g., "EMP-25-00001"

      console.log(`Fetching notices for role: ${userRole}, userID: ${userID}`);

      // 2. Build the API URL with query parameters
      const params = new URLSearchParams({
          role: userRole,
          ...(userID && { userID: userID }) // Only add userID if it exists
      });
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/announcements/all?${params.toString()}`;

      console.log("Fetching from:", apiUrl);

      try {
        const response = await fetch(apiUrl);

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Server returned ${response.status}: ${errorText}`);
        }

        const responseBody = await response.json();

        // 3. Process data directly - NO MORE CLIENT-SIDE FILTERING
        if (responseBody && responseBody.success === true) {
            const announcementsFromServer = responseBody.announcements || [];
            console.log(`Received ${announcementsFromServer.length} pre-filtered announcements from server.`);

            // Format the announcements that the server has already approved for us
            const formatted = announcementsFromServer.map(announcement => ({
              id: announcement._id || "unknown-id",
              title: announcement.subject || "No Title",
              message: announcement.content || "No Content",
              postedBy: announcement.announcer || "System",
              postedDate: announcement.createdAt
                ? new Date(announcement.createdAt).toLocaleString()
                : "Unknown Date",
              type: announcement.priority || "normal",
              audience: announcement.audience || "General",
              // You can still add the isSpecific flag for UI purposes if needed
              isSpecific: !!announcement.specificReceiver
            }));

            setNotices(formatted);
        } else {
          console.error("API returned error:", responseBody);
          setError(responseBody?.message || "Unknown server error");
        }
      } catch (error) {
        console.error("Error fetching notices:", error);
        setError(`Failed to load announcements. Please try again later.`);
      } finally {
        setLoading(false);
      }
    };

    fetchNotices();
  }, []); // Runs once on component mount

  // This function is no longer needed on the frontend as the logic is now on the server.
  // const getRoleAccessMap = (role) => { ... };

  const handlePrevious = () => {
    setCurrentNoticeIndex(prev =>
      prev === 0 ? (notices.length > 0 ? notices.length - 1 : 0) : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentNoticeIndex(prev =>
      prev === notices.length - 1 ? 0 : prev + 1
    );
  };

  if (loading) {
    return <div className="noticeboard"><p>Loading notices...</p></div>;
  }

  if (error) {
    return (
      <div className="noticeboard">
        <p className="error-message" style={{ color: 'red' }}>{error}</p>
      </div>
    );
  }

  if (notices.length === 0) {
    return (
      <div className="noticeboard">
        <div className="notification-count">0</div>
        <div className="noticeboard-header">
          <p className="subheading">Notice Board</p>
          <div className="arrows">
            <div className="arrow-btn disabled"><MdOutlineKeyboardArrowLeft style={{ opacity: 0.5 }} /></div>
            <div className="arrow-btn disabled"><MdOutlineKeyboardArrowRight style={{ opacity: 0.5 }} /></div>
          </div>
        </div>
        <div className="divider" />
        <div className="notice-item">
          <div className="notice-badge">NOTICE</div>
          <h3 className="notice-title">No New Announcements</h3>
          <p className="notice-message">There are no announcements for you at this time.</p>
          <div className="postedby">
            <div className="postedby-pfp"><FaUser style={{ fontSize: '1.5rem', color: '#95a5a6' }} /></div>
            <div className="postedby-descrip">
              <p className="postedby-name">System</p>
              <p>{new Date().toLocaleString()}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentNotice = notices[currentNoticeIndex];

  return (
    <div className="noticeboard">
      <div className="notification-count">{notices.length}</div>
      <div className="noticeboard-header">
        <p className="subheading">Notice Board</p>
        <div className="arrows">
          <div className="arrow-btn" onClick={handlePrevious}><MdOutlineKeyboardArrowLeft /></div>
          <div className="arrow-btn" onClick={handleNext}><MdOutlineKeyboardArrowRight /></div>
        </div>
      </div>
      <div className="divider" />
      <div className="notice-item">
        <div className={`notice-badge ${currentNotice.type === 'important' ? 'urgent' : currentNotice.type === 'info' ? 'info' : ''}`}>
          {currentNotice.type?.toUpperCase() || 'NOTICE'}
        </div>
        <h3 className="notice-title">{currentNotice.title}</h3>
        <p className="notice-message">{currentNotice.message}</p>
        <div className="postedby">
          <div className="postedby-pfp"><FaUser style={{ fontSize: '1.5rem', color: '#95a5a6' }} /></div>
          <div className="postedby-descrip">
            <p className="postedby-name">{currentNotice.postedBy}</p>
            <p>{currentNotice.postedDate}</p>
          </div>
        </div>
        <p className="audience-tag" style={{ fontSize: '0.8rem', color: '#666', fontStyle: 'italic', marginTop: '8px' }}>
          For: {currentNotice.isSpecific ? 'You (Specific Message)' : currentNotice.audience}
        </p>
      </div>
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
        {notices.map((_, index) => (
          <MdCircle
            key={index}
            style={{ margin: '0 4px', fontSize: '10px', color: index === currentNoticeIndex ? '#3498db' : '#ddd', cursor: 'pointer' }}
            onClick={() => setCurrentNoticeIndex(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default NoticeBoard;